/**
 * Modular pricing — single source of truth.
 *
 * Every price, tier→module mapping, and volume-discount rule for the modular
 * land-and-expand model lives HERE and only here. The pricing page, the live
 * bill calculator, the Stripe subscription builder, and the entitlement layer
 * all derive from these constants — never hardcode a price or a plan→feature
 * mapping anywhere else.
 *
 * All money amounts are integer US cents (convention of `shared/fees.ts`).
 *
 * The model:
 *   - Two account kinds picked at signup: `operator` | `property_manager`.
 *   - Two per-room base tiers at identical prices for both kinds — the kind
 *     only changes the display label and which add-ons are purchasable
 *     (operators: Revenue + Research; PMs: Owner Portals + CPM listing).
 *   - Essentials = daily-operations core. Pro = Essentials + every operator
 *     module + Devices, priced below the à-la-carte sum (funnel invariant).
 *   - Rooms bill as TWO quantities: on-PadSplit and off-PadSplit. Off-PadSplit
 *     rooms carry a flat surcharge (they need PadPulse's own Member Portals).
 *   - Volume discount applies to the ENTIRE bill, keyed to combined rooms.
 */

export const ACCOUNT_KINDS = ["operator", "property_manager"] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];

export const PLAN_TIERS = ["essentials", "pro"] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

/** Per-room monthly price for each base tier, in cents. Identical across account kinds. */
export const PLAN_TIER_PER_ROOM_CENTS: Record<PlanTier, number> = {
  essentials: 700,
  pro: 1200,
};

/** Four display names over two price tiers — the kind only swaps the label. */
export const PLAN_TIER_LABELS: Record<AccountKind, Record<PlanTier, string>> = {
  operator: { essentials: "Operator Essentials", pro: "Operator Pro" },
  property_manager: { essentials: "PM Essentials", pro: "PM Pro" },
};

/** Feature keys included in the Essentials daily-operations core (both kinds). */
export const ESSENTIALS_CORE_FEATURES = [
  "properties",
  "members",
  "vehicles",
  "booking_requests",
  "messages",
  "tasks",
  "todos",
] as const;

/**
 * À-la-carte operator modules: stack on Essentials per-room, every one of them
 * is already included in Pro. Purchasable by BOTH account kinds (only the
 * Revenue/Research add-ons are operator-exclusive).
 */
export const OPERATOR_MODULES = {
  supplies: {
    perRoomCents: 100,
    label: "Supplies",
    description:
      "Track supply items across your houses, log orders, and set recurring reorder schedules so essentials like paper goods and cleaning products never run out.",
  },
  collections: {
    perRoomCents: 100,
    label: "Collections",
    comingSoon: "Coming September 1",
    description:
      "Track delinquent members and collection status across your portfolio, with balances synced automatically from PadSplit so nothing slips through the cracks.",
  },
  cleaning: {
    perRoomCents: 200,
    label: "Cleaning Management",
    description:
      "Run member cleaning schedules with automated reminders, plus cleaning-service request forms, so shared spaces stay on rotation without manual chasing.",
  },
  dynamic_pricing: {
    perRoomCents: 100,
    label: "Dynamic Pricing",
    description:
      "Build pricing models with rules that adjust room rates automatically and push the updated rates straight to your PadSplit listings.",
  },
  marketing: {
    perRoomCents: 100,
    label: "Marketing",
    description:
      "A marketing hub to promote your rooms and drive occupancy, including your own direct booking landing page, third-party listing tools, and referral tracking.",
  },
  ai_msg_enhancer: {
    perRoomCents: 100,
    label: "AI Message Enhancer",
    description:
      "AI drafts and improves your replies to member messages — turn a rough note into a clear, professional response in one click.",
  },
  analytics: {
    perRoomCents: 100,
    label: "Analytics",
    description:
      "Portfolio analytics dashboards covering occupancy trends, room-level performance, and member health across all your properties.",
  },
} as const;

/**
 * Devices stack on Essentials per-room and are included in Pro. Other
 * third-party locks additionally carry a $2.50/lock/mo pass-through billed
 * elsewhere (TTLock/Sifely/PadPulse locks integrate free) — that is a
 * device-level pass-through, not part of this per-room price.
 */
export const DEVICE_MODULES = {
  locks: {
    perRoomCents: 200,
    label: "Smart Locks",
    description:
      "Control TTLock, Sifely, and many other smart locks from PadPulse — issue and revoke member door codes automatically as people move in and out.",
  },
  thermostats: {
    perRoomCents: 100,
    label: "Thermostats",
    description:
      "Monitor connected thermostats across your properties and apply temperature presets to keep houses comfortable and utility costs in check.",
  },
} as const;

export type OperatorModuleKey = keyof typeof OPERATOR_MODULES;
export type DeviceModuleKey = keyof typeof DEVICE_MODULES;
export type ModuleKey = OperatorModuleKey | DeviceModuleKey;

export const OPERATOR_MODULE_KEYS = Object.keys(OPERATOR_MODULES) as OperatorModuleKey[];
export const DEVICE_MODULE_KEYS = Object.keys(DEVICE_MODULES) as DeviceModuleKey[];
export const MODULE_KEYS: readonly ModuleKey[] = [...OPERATOR_MODULE_KEYS, ...DEVICE_MODULE_KEYS];

export function isModuleKey(key: string): key is ModuleKey {
  return (MODULE_KEYS as readonly string[]).includes(key);
}

export function modulePerRoomCents(key: ModuleKey): number {
  return key in OPERATOR_MODULES
    ? OPERATOR_MODULES[key as OperatorModuleKey].perRoomCents
    : DEVICE_MODULES[key as DeviceModuleKey].perRoomCents;
}

export function moduleLabel(key: ModuleKey): string {
  return key in OPERATOR_MODULES
    ? OPERATOR_MODULES[key as OperatorModuleKey].label
    : DEVICE_MODULES[key as DeviceModuleKey].label;
}

/**
 * "Coming <date>" launch label for a not-yet-purchasable module, or null when
 * the module is live. While set, the module is visible on the calculator but
 * unselectable, and every purchase surface (computeBill, the plan PUT, the
 * Stripe item builder) refuses it. Delete the config flag at launch.
 */
export function moduleComingSoonLabel(key: ModuleKey): string | null {
  const entry =
    key in OPERATOR_MODULES
      ? OPERATOR_MODULES[key as OperatorModuleKey]
      : DEVICE_MODULES[key as DeviceModuleKey];
  return (entry as { comingSoon?: string }).comingSoon ?? null;
}

/** Module keys currently purchasable (excludes coming-soon entries). */
export const PURCHASABLE_MODULE_KEYS: readonly ModuleKey[] = MODULE_KEYS.filter(
  (k) => moduleComingSoonLabel(k) == null,
);

/** Same as moduleComingSoonLabel for the operator add-ons (revenue/research). */
export function addonComingSoonLabel(key: OperatorAddonKey): string | null {
  return (OPERATOR_ADDONS[key] as { comingSoon?: string }).comingSoon ?? null;
}

/**
 * Strip coming-soon selections from PERSISTED account state before quoting or
 * building Stripe items — an account that toggled a module before it went
 * coming-soon must not crash its plan page or bill for it. New selections are
 * rejected loudly instead (computeBill throws COMING_SOON).
 */
export function sanitizeComingSoonSelection<T extends { modules?: readonly string[]; revenueAddon?: boolean }>(
  input: T,
): T {
  return {
    ...input,
    modules: (input.modules ?? []).filter((m) => !isModuleKey(m) || moduleComingSoonLabel(m) == null),
    revenueAddon: input.revenueAddon && addonComingSoonLabel("revenue") == null ? input.revenueAddon : false,
  };
}

/**
 * Flat surcharge per off-PadSplit room, on top of that room's other per-room
 * charges. Off-PadSplit members have no PadSplit-native portal, so PadPulse
 * provides Member Portals for them. On-PadSplit rooms never carry this.
 */
export const OFF_PADSPLIT_SURCHARGE_CENTS = 300;

/**
 * Operator add-ons — not purchasable on PM accounts and NOT included in
 * operator Pro. Revenue is the Mercury-connected income/expense suite (base
 * PadSplit income tracking is free on every plan); PM Pro INCLUDES it at no
 * extra charge (see computeEntitlements), PM Essentials has no path to it.
 * Research unlocks the acquisition toolkit and stays operator-only.
 */
export const OPERATOR_ADDONS = {
  revenue: {
    flatCents: 2900,
    perRoomCents: 100,
    label: "Revenue (Mercury P&L)",
    comingSoon: "Coming August 1",
    description:
      "The Mercury-connected income & expense suite: bank transactions flow in automatically and roll up into a full P&L across your portfolio. Base PadSplit income is free on every plan.",
  },
  research: {
    flatCents: 2900,
    perRoomCents: 0,
    label: "Research (Connections)",
    description:
      "Unlocks the full acquisition toolkit: Market Research (city and market data), Buy Box (automated property search against your buying criteria), Underwriting (deal analysis), Buy (your deal pipeline), Sell (disposition tracking), and Renovate (renovation project tracking).",
  },
} as const;
export type OperatorAddonKey = keyof typeof OPERATOR_ADDONS;

/** PM-only line items — hidden on operator accounts. */
export const PM_ADDONS = {
  owner_portals: { perOwnerCents: 2000, label: "Owner Portals" },
  cpm_listing: { flatCents: 19900, label: "Certified PM Listing" },
} as const;
export type PmAddonKey = keyof typeof PM_ADDONS;

/**
 * Vendor Marketplace privacy fee: $19/mo per vendor a User keeps OFF the
 * shared marketplace (private to their portfolio), charged to that User on
 * their main subscription. Vendor payouts are unaffected. Distinct from
 * MARKETPLACE_JOB_FEE_CENTS in `shared/fees.ts` (the vendor-paid $19
 * pay-on-job fee) — same dollar figure by coincidence, different fee with a
 * different payer; keep the constants separate.
 */
export const VENDOR_PRIVACY_FEE_CENTS = 1900;

/**
 * Owner-paid products (sold to individual owners on a PM's book). An owner
 * with ANY ONE of these active has their $20/mo Owner Portal fee waived for
 * as long as they stay subscribed — tracked per owner, never account-wide.
 */
export const OWNER_PRODUCT_KEYS = ["research", "revenue", "analytics"] as const;
export type OwnerProductKey = (typeof OWNER_PRODUCT_KEYS)[number];

/**
 * Volume discount off the GRAND TOTAL (every line, including billed Owner
 * Portals, flat add-ons, and the vendor privacy fee), keyed to the COMBINED
 * on+off-PadSplit room count. Ordered highest tier first.
 */
export const VOLUME_DISCOUNT_TIERS = [
  { minRooms: 2000, percent: 25 },
  { minRooms: 1500, percent: 21 },
  { minRooms: 1000, percent: 17 },
  { minRooms: 500, percent: 13 },
  { minRooms: 250, percent: 9 },
  { minRooms: 100, percent: 5 },
] as const;

export const MAX_VOLUME_DISCOUNT_PERCENT = VOLUME_DISCOUNT_TIERS[0].percent;

/** Highest discount tier whose minRooms is met by the combined room count. */
export function volumeDiscountPercent(combinedRooms: number): number {
  const rooms = clampCount(combinedRooms);
  for (const tier of VOLUME_DISCOUNT_TIERS) {
    if (rooms >= tier.minRooms) return tier.percent;
  }
  return 0;
}

/**
 * PadSplit charges hosts $5/room/mo; discounted Essentials must always stay
 * above that floor (floor invariant below) so PadPulse is never priced at or
 * under the marketplace's own per-room fee.
 */
export const PADSPLIT_FLOOR_PER_ROOM_CENTS = 500;

export const MODULAR_TRIAL_DAYS = 14;
export const GUARANTEE_DAYS = 30;

// ─── Bill calculator ──────────────────────────────────────────────────────────

export type ModularPricingErrorCode =
  | "UNKNOWN_MODULE"
  | "OPERATOR_ONLY_ADDON"
  | "PM_ONLY_ADDON"
  | "COMING_SOON";

export class ModularPricingError extends Error {
  code: ModularPricingErrorCode;
  constructor(code: ModularPricingErrorCode, message: string) {
    super(message);
    this.name = "ModularPricingError";
    this.code = code;
  }
}

export interface BillInput {
  accountKind: AccountKind;
  planTier: PlanTier;
  /** Selected à-la-carte modules. Ignored on Pro (all included). */
  modules?: readonly string[];
  roomsOnPadsplit: number;
  roomsOffPadsplit: number;
  /** Operator-only add-ons — throw for property_manager accounts. */
  revenueAddon?: boolean;
  researchAddon?: boolean;
  /** PM-only — owners with portal access. Throw when > 0 on operator accounts. */
  ownerPortalCount?: number;
  /** Owners whose portal fee is waived by an active owner-paid product. */
  waivedOwnerCount?: number;
  /** PM-only — throw when true on operator accounts. */
  cpmListing?: boolean;
  /** Vendors this User keeps off the shared marketplace ($19/mo each). */
  offMarketplaceVendorCount?: number;
}

export interface BillLine {
  /** Stable machine key, e.g. "base_on_padsplit", "module_cleaning". */
  key: string;
  label: string;
  unitCents: number;
  quantity: number;
  amountCents: number;
}

export interface BillBreakdown {
  lines: BillLine[];
  combinedRooms: number;
  subtotalCents: number;
  volumeDiscountPercent: number;
  volumeDiscountCents: number;
  totalCents: number;
  /** Post-discount per-room monthly rate for each pool (room-level charges only). */
  effectivePerRoomOnCents: number;
  effectivePerRoomOffCents: number;
  billedOwnerCount: number;
  waivedOwnerCount: number;
  /**
   * Essentials only: how much LESS the same selection would cost on Pro
   * (positive = switching to Pro saves this much per month). Null on Pro.
   */
  proSavingsCents: number | null;
}

function clampCount(value: number | undefined): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.max(0, Math.floor(value))
    : 0;
}

/**
 * Pure bill calculator — the ONE place the monthly total is derived. The
 * pricing page runs it client-side for live feedback; the server runs the
 * same function for authoritative quotes and to build the Stripe
 * subscription items. Throws ModularPricingError on selections invalid for
 * the account kind so bad input can never silently price to $0.
 */
export function computeBill(input: BillInput): BillBreakdown {
  const { accountKind, planTier } = input;
  const roomsOn = clampCount(input.roomsOnPadsplit);
  const roomsOff = clampCount(input.roomsOffPadsplit);
  const combinedRooms = roomsOn + roomsOff;

  const ownerPortalCount = clampCount(input.ownerPortalCount);
  const waivedOwnerCount = clampCount(input.waivedOwnerCount);
  const vendorCount = clampCount(input.offMarketplaceVendorCount);

  if (accountKind === "property_manager" && (input.revenueAddon || input.researchAddon)) {
    throw new ModularPricingError(
      "OPERATOR_ONLY_ADDON",
      "Revenue and Research are operator-only add-ons",
    );
  }
  if (accountKind === "operator" && (ownerPortalCount > 0 || waivedOwnerCount > 0 || input.cpmListing)) {
    throw new ModularPricingError(
      "PM_ONLY_ADDON",
      "Owner Portals and the CPM listing are property-manager-only",
    );
  }

  // Unknown keys are rejected on every tier; selections only PRICE on
  // Essentials (Pro already includes every module).
  const selectedModules: ModuleKey[] = [];
  for (const raw of input.modules ?? []) {
    if (!isModuleKey(raw)) {
      throw new ModularPricingError("UNKNOWN_MODULE", `Unknown module key: ${raw}`);
    }
    const soon = moduleComingSoonLabel(raw);
    if (soon) {
      throw new ModularPricingError("COMING_SOON", `${moduleLabel(raw)} is not yet available (${soon})`);
    }
    if (planTier === "essentials" && !selectedModules.includes(raw)) selectedModules.push(raw);
  }

  const tierRate = PLAN_TIER_PER_ROOM_CENTS[planTier];
  const tierLabel = PLAN_TIER_LABELS[accountKind][planTier];
  const lines: BillLine[] = [];
  const pushLine = (key: string, label: string, unitCents: number, quantity: number) => {
    if (quantity <= 0 || unitCents <= 0) return;
    lines.push({ key, label, unitCents, quantity, amountCents: unitCents * quantity });
  };

  pushLine("base_on_padsplit", `${tierLabel} — rooms on PadSplit`, tierRate, roomsOn);
  pushLine("base_off_padsplit", `${tierLabel} — rooms off PadSplit`, tierRate, roomsOff);
  pushLine(
    "off_padsplit_surcharge",
    "Member Portals surcharge — rooms off PadSplit",
    OFF_PADSPLIT_SURCHARGE_CENTS,
    roomsOff,
  );

  for (const key of selectedModules) {
    pushLine(`module_${key}`, moduleLabel(key), modulePerRoomCents(key), combinedRooms);
  }

  if (input.revenueAddon && addonComingSoonLabel("revenue")) {
    throw new ModularPricingError(
      "COMING_SOON",
      `${OPERATOR_ADDONS.revenue.label} is not yet available (${addonComingSoonLabel("revenue")})`,
    );
  }
  if (input.revenueAddon) {
    pushLine("revenue_flat", `${OPERATOR_ADDONS.revenue.label} — base`, OPERATOR_ADDONS.revenue.flatCents, 1);
    pushLine("revenue_per_room", `${OPERATOR_ADDONS.revenue.label} — per room`, OPERATOR_ADDONS.revenue.perRoomCents, combinedRooms);
  }
  if (input.researchAddon) {
    pushLine("research_flat", OPERATOR_ADDONS.research.label, OPERATOR_ADDONS.research.flatCents, 1);
  }

  const billedOwnerCount = Math.max(0, ownerPortalCount - waivedOwnerCount);
  pushLine("owner_portals", PM_ADDONS.owner_portals.label, PM_ADDONS.owner_portals.perOwnerCents, billedOwnerCount);
  if (input.cpmListing) {
    pushLine("cpm_listing", PM_ADDONS.cpm_listing.label, PM_ADDONS.cpm_listing.flatCents, 1);
  }

  pushLine("vendor_privacy", "Vendors kept off Marketplace", VENDOR_PRIVACY_FEE_CENTS, vendorCount);

  const subtotalCents = lines.reduce((sum, line) => sum + line.amountCents, 0);
  const discountPercent = volumeDiscountPercent(combinedRooms);
  const volumeDiscountCents = Math.round((subtotalCents * discountPercent) / 100);
  const totalCents = subtotalCents - volumeDiscountCents;

  const perRoomModulesCents = selectedModules.reduce((sum, key) => sum + modulePerRoomCents(key), 0);
  const perRoomSubtotalCents = tierRate + perRoomModulesCents;
  const discountFactor = (100 - discountPercent) / 100;
  const effectivePerRoomOnCents = Math.round(perRoomSubtotalCents * discountFactor);
  const effectivePerRoomOffCents = Math.round(
    (perRoomSubtotalCents + OFF_PADSPLIT_SURCHARGE_CENTS) * discountFactor,
  );

  let proSavingsCents: number | null = null;
  if (planTier === "essentials") {
    const proTotal = computeBill({ ...input, planTier: "pro", modules: [] }).totalCents;
    proSavingsCents = totalCents - proTotal;
  }

  return {
    lines,
    combinedRooms,
    subtotalCents,
    volumeDiscountPercent: discountPercent,
    volumeDiscountCents,
    totalCents,
    effectivePerRoomOnCents,
    effectivePerRoomOffCents,
    billedOwnerCount,
    waivedOwnerCount: Math.min(waivedOwnerCount, ownerPortalCount),
    proSavingsCents,
  };
}

// ─── Stripe billing components ────────────────────────────────────────────────

/**
 * What drives a component's subscription-item quantity:
 *   combined_rooms          — billable rooms, on + off PadSplit
 *   off_rooms               — billable off-PadSplit rooms only (the surcharge)
 *   billed_owners           — portal-invited owners minus waived owners
 *   off_marketplace_vendors — vendors flagged off the shared marketplace
 *   flat                    — always quantity 1
 */
export type ModularQuantityBasis =
  | "combined_rooms"
  | "off_rooms"
  | "billed_owners"
  | "off_marketplace_vendors"
  | "flat";

export interface ModularBillingComponent {
  /** Catalog key — the `billing_price_catalog.plan` column + product metadata. */
  key: string;
  /** Stripe price lookup key (`padpulse_<key>_monthly`). */
  lookupKey: string;
  label: string;
  unitCents: number;
  quantityBasis: ModularQuantityBasis;
}

function component(
  key: string,
  label: string,
  unitCents: number,
  quantityBasis: ModularQuantityBasis,
): ModularBillingComponent {
  return { key, lookupKey: `padpulse_${key}_monthly`, label, unitCents, quantityBasis };
}

/**
 * Every Stripe price the modular model needs — one product+price per
 * component, all monthly, quantities reconciled by
 * server/lib/modular-subscription.ts. Derived from the pricing constants
 * above so the catalog can never drift from the displayed prices.
 */
export function modularBillingComponents(): ModularBillingComponent[] {
  return [
    component("mod_essentials", "PadPulse Essentials", PLAN_TIER_PER_ROOM_CENTS.essentials, "combined_rooms"),
    component("mod_pro", "PadPulse Pro", PLAN_TIER_PER_ROOM_CENTS.pro, "combined_rooms"),
    component("mod_offps_surcharge", "Member Portals surcharge (off-PadSplit rooms)", OFF_PADSPLIT_SURCHARGE_CENTS, "off_rooms"),
    ...OPERATOR_MODULE_KEYS.map((key) =>
      component(`mod_${key}`, `PadPulse ${OPERATOR_MODULES[key].label}`, OPERATOR_MODULES[key].perRoomCents, "combined_rooms"),
    ),
    ...DEVICE_MODULE_KEYS.map((key) =>
      component(`mod_${key}`, `PadPulse ${DEVICE_MODULES[key].label}`, DEVICE_MODULES[key].perRoomCents, "combined_rooms"),
    ),
    component("mod_revenue_flat", "PadPulse Revenue — base", OPERATOR_ADDONS.revenue.flatCents, "flat"),
    component("mod_revenue_perroom", "PadPulse Revenue — per room", OPERATOR_ADDONS.revenue.perRoomCents, "combined_rooms"),
    component("mod_research_flat", "PadPulse Research", OPERATOR_ADDONS.research.flatCents, "flat"),
    component("mod_owner_portals", "PadPulse Owner Portals", PM_ADDONS.owner_portals.perOwnerCents, "billed_owners"),
    component("mod_cpm_listing", "PadPulse Certified PM Listing", PM_ADDONS.cpm_listing.flatCents, "flat"),
    component("mod_vendor_privacy", "Vendors kept off Marketplace", VENDOR_PRIVACY_FEE_CENTS, "off_marketplace_vendors"),
  ];
}

/** Module key → its billing-component key (both device and operator modules). */
export function componentKeyForModule(key: ModuleKey): string {
  return `mod_${key}`;
}

/** Stripe coupon id for a volume-discount tier (created by the bootstrap script). */
export function volumeCouponId(percent: number): string | null {
  return percent > 0 ? `padpulse_vol_${percent}` : null;
}

// ─── Legacy tier migration ────────────────────────────────────────────────────

export interface ModularAssignment {
  accountKind: AccountKind;
  planTier: PlanTier;
  /** Carry the legacy Research plan over as the Research add-on when it was live. */
  researchAddonActive: boolean;
}

/**
 * Maps a legacy `selected_plan` tier onto the modular model. Used by the
 * one-time cutover backfill (scripts/backfill-modular-pricing.ts) AND by
 * signup/approval paths while the client still submits legacy plan names
 * (until the Phase-6 UI rework sends `accountKind` directly).
 *
 *   Enterprise, PMPro              → property_manager / pro
 *   Professional, Founder25/50/100 → operator / pro
 *   Research, Interest             → operator / essentials (+ research add-on
 *                                    when the account was actively using it)
 *   Host, Growth, anything else    → operator / essentials
 */
export function modularAssignmentForLegacyPlan(
  selectedPlan: string | null,
  subscriptionStatus: string | null,
): ModularAssignment {
  const plan = selectedPlan ?? "";
  const live = ["active", "trialing", "comped"].includes(subscriptionStatus ?? "");
  switch (plan) {
    case "Enterprise":
    case "PMPro":
      return { accountKind: "property_manager", planTier: "pro", researchAddonActive: false };
    case "Professional":
    case "Founder25":
    case "Founder50":
    case "Founder100":
      return { accountKind: "operator", planTier: "pro", researchAddonActive: false };
    case "Research":
    case "Interest":
      return { accountKind: "operator", planTier: "essentials", researchAddonActive: live };
    default:
      return { accountKind: "operator", planTier: "essentials", researchAddonActive: false };
  }
}

// ─── Config invariants ────────────────────────────────────────────────────────

export interface PricingInvariantInput {
  essentialsPerRoomCents: number;
  proPerRoomCents: number;
  operatorModulesTotalCents: number;
  deviceModulesTotalCents: number;
  maxDiscountPercent: number;
  floorPerRoomCents: number;
}

function liveInvariantInput(): PricingInvariantInput {
  return {
    essentialsPerRoomCents: PLAN_TIER_PER_ROOM_CENTS.essentials,
    proPerRoomCents: PLAN_TIER_PER_ROOM_CENTS.pro,
    operatorModulesTotalCents: OPERATOR_MODULE_KEYS.reduce(
      (sum, key) => sum + OPERATOR_MODULES[key].perRoomCents,
      0,
    ),
    deviceModulesTotalCents: DEVICE_MODULE_KEYS.reduce(
      (sum, key) => sum + DEVICE_MODULES[key].perRoomCents,
      0,
    ),
    maxDiscountPercent: MAX_VOLUME_DISCOUNT_PERCENT,
    floorPerRoomCents: PADSPLIT_FLOOR_PER_ROOM_CENTS,
  };
}

/**
 * Two invariants that keep the model coherent; violating either is a config
 * bug that must crash at boot rather than ship:
 *
 *   Funnel: Essentials + every operator module + Devices must cost MORE than
 *   Pro ($7 + $8 + $3 = $18 > $12), or Pro stops being the obvious upgrade.
 *
 *   Floor: fully-discounted Essentials must stay above PadSplit's $5/room
 *   ($7 × 0.75 = $5.25 > $5.00).
 *
 * `input` is injectable so tests can prove the assertions actually throw.
 */
export function assertPricingInvariants(input: PricingInvariantInput = liveInvariantInput()): void {
  const alaCarteCents =
    input.essentialsPerRoomCents + input.operatorModulesTotalCents + input.deviceModulesTotalCents;
  if (alaCarteCents <= input.proPerRoomCents) {
    throw new Error(
      `Pricing funnel invariant violated: à-la-carte Essentials (${alaCarteCents}¢/room) must exceed Pro (${input.proPerRoomCents}¢/room)`,
    );
  }
  const minTierCents = Math.min(input.essentialsPerRoomCents, input.proPerRoomCents);
  const discountedFloorCents = minTierCents * (1 - input.maxDiscountPercent / 100);
  if (discountedFloorCents <= input.floorPerRoomCents) {
    throw new Error(
      `Pricing floor invariant violated: max-discounted per-room price (${discountedFloorCents}¢) must exceed the PadSplit floor (${input.floorPerRoomCents}¢)`,
    );
  }
}

assertPricingInvariants();
